// src/index.js
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Apollo Client imports
import { ApolloProvider } from '@apollo/client';
import client from './apolloClient';

// Application imports
import { CartProvider } from './contexts/CartContext';
import App from './App';
import { setConfig } from './configStore';
import ErrorComponent from './ErrorComponent';
import { consoleDebug, getSentryDsn } from './util';
import './style/main.scss';

// Library imports
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Contexts
import { ConfigProvider } from './contexts/ConfigContext';
import { AppStateProvider } from './contexts/AppStateContext';

// Constants
const ROOT_SELECTOR_PREFIX = 'range-headless-root-';
const DATASET_ATTRIBUTE = 'rangeheadless';

/**
 * Main initialization function
 */
function initializeApp() {    
    let globalConfig = null;
    const shortcodeData = {};

    try {
        // 1. Initialize Sentry (optional)
        try {
            const dsn = getSentryDsn();
            if (dsn) {
                Sentry.init({
                    environment: process.env.NODE_ENV || 'production',
                    dsn: dsn,
                    release: process.env.SENTRY_RELEASE || 'range-headless-react@2.0.0',
                    integrations: [],
                    beforeBreadcrumb: () => null,
                    tracePropagationTargets: [/.+\/wp-json\/.+/, rangeHeadlessApi?.apiEndpoint],
                    beforeSend(event) {
                        if (event.tags?.manually_captured === true ||
                            event.tags?.error_type === 'react_error_boundary') {
                            return event;
                        }
                        return null;
                    }
                });
            }
        } catch (e) {
            console.warn('Sentry initialization skipped:', e);
        }

        // 2. Find root elements
        const roots = document.querySelectorAll(`[id^="${ROOT_SELECTOR_PREFIX}"]`);
        
        if (roots.length === 0) {
            console.warn('No Range Headless root elements found.');
            return;
        }

        // 3. Collect data from root elements
        roots.forEach((root) => {
            try {
                const rawData = root.dataset[DATASET_ATTRIBUTE];
                if (rawData) {
                    const parsedData = JSON.parse(rawData);
                    shortcodeData[root.id] = parsedData;
                    
                    if (parsedData.hasGlobal && parsedData.globalConfig) {
                        globalConfig = parsedData.globalConfig;
                    }
                }
            } catch (error) {
                console.error(`Failed to parse data for ${root.id}:`, error);
            }
        });

        // 4. Set global config
        if (!globalConfig && Object.keys(shortcodeData).length > 0) {
            const firstRootData = Object.values(shortcodeData)[0];
            if (firstRootData?.globalConfig) {
                globalConfig = firstRootData.globalConfig;
            }
        }

        if (globalConfig) {
            setConfig(globalConfig);
        }

        // 5. Setup React Query
        const queryClient = new QueryClient({
            defaultOptions: {
                queries: {
                    retry: 2,
                    gcTime: 5 * 60 * 1000,
                    cacheTime: 30 * 60 * 1000,
                    refetchOnWindowFocus: false,
                    refetchOnReconnect: false,
                },
            },
        });

        // 6. Setup Router
        const router = createBrowserRouter([
            {
                path: '/:viewName?/:locationSlug?/:categoryType?/:categoryName?/:specialCategoryName?',
                element: (
                    <Sentry.ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                        <ConfigProvider
                            globalConfig={globalConfig}
                            shortcodeData={shortcodeData}
                            fallback={rangeHeadlessApi?.fallback || {}}
                        >
                            <AppStateProvider>
                                <CartProvider retailerId={null}>
                                    <App
                                        shortcodeData={shortcodeData}
                                        globalConfig={globalConfig}
                                    />
                                </CartProvider>
                            </AppStateProvider>
                        </ConfigProvider>
                    </Sentry.ErrorBoundary>
                ),
            },
        ]);

        // 7. Render the app
        const primaryRoot = roots[0];
        
        const root = createRoot(primaryRoot);
        root.render(
            <StrictMode>
                <ApolloProvider client={client}>
                    <QueryClientProvider client={queryClient}>
                        <RouterProvider router={router} />
                    </QueryClientProvider>
                </ApolloProvider>
            </StrictMode>
        );
    } catch (error) {
        console.error('Failed to initialize Range Headless:', error);
        
        // Simple fallback message
        const roots = document.querySelectorAll(`[id^="${ROOT_SELECTOR_PREFIX}"]`);
        roots.forEach(root => {
            root.innerHTML = `
                <div style="text-align: center; padding: 20px; margin: 20px; border: 1px solid #ddd;">
                    <h3>Unable to load menu</h3>
                    <p>Please refresh the page or <a href="/backup-menu/">click here</a></p>
                </div>
            `;
        });
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
} else {
    initializeApp();
}
